import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFolderNameFragment } from '@frameio/graphql-client/generated';
import { useResourceNotFoundToast } from '../components/ExportLocation';
import { useGetFolderAssetForExportLocationQuery } from '../gql/queries/generated/GetFolderAssetForExportLocation';
import { normalizeRootFolderName } from '../utils/normalizeRootFolderName';
import { useProjectLocationSelector } from './useProjectLocationSelector';

/**
 * Base settings shape that all host export settings must satisfy.
 * Hosts extend this with their own fields (e.g., Premiere adds `presetId`,
 * AE adds `outputModuleTemplate`).
 */
type BaseExportSettings = {
  assetFolderId?: string | null;
  assetFolderName?: string | null;
  localCopyEnabled?: boolean | null;
  localFolderPath?: string | null;
  exportMarkersAsCommentsEnabled?: boolean | null;
};

type BaseExportSettingsWithTypename = BaseExportSettings & {
  __typename?: string;
};

type UseExportSettingsBaseOptions<
  TSettings extends BaseExportSettingsWithTypename,
> = {
  /** Default settings when nothing is persisted yet. */
  defaultSettings: TSettings;
  /** UXP-fetched settings (from host-specific GQL query). */
  uxpStorage: TSettings | null | undefined;
  /** Whether the server settings are still loading. */
  loading: boolean;
  /** Persist settings to the server (host-specific GQL mutation). */
  storeMutation: (
    input: Omit<TSettings, '__typename'>
  ) => Promise<TSettings | null>;
  /** Optional folder ID override (e.g., from version-stack mode). */
  overrideFolderId?: string;
};

export type UseExportSettingsManagerReturnType<
  TSettings extends BaseExportSettingsWithTypename,
> = TSettings & {
  loading: boolean;
  updateExportSettings: (partial: Partial<TSettings>) => void;
  storeExportSettings: (partial?: Partial<TSettings>) => Promise<void>;
  projectId: string | null;
  onProjectIdChange: (projectId: string | null) => void;
};

export const useExportSettingsManager = <
  TSettings extends BaseExportSettingsWithTypename,
>({
  defaultSettings,
  uxpStorage,
  loading,
  storeMutation,
  overrideFolderId,
}: UseExportSettingsBaseOptions<TSettings>): UseExportSettingsManagerReturnType<TSettings> => {
  const { data: overrideFolder } = useFolderNameFragment({
    from: { __typename: 'Asset', id: overrideFolderId },
  });

  const [pendingSettings, setPendingSettings] = useState<TSettings | null>(
    null
  );

  const { data: selectedFolder } = useFolderNameFragment({
    from: {
      __typename: 'Asset',
      id: pendingSettings?.assetFolderId ?? null,
    },
  });

  const { projectId, projectName, onProjectIdChange } =
    useProjectLocationSelector();

  const settings = useMemo((): TSettings => {
    if (pendingSettings) {
      return {
        ...pendingSettings,
        assetFolderName:
          pendingSettings.assetFolderName ??
          normalizeRootFolderName(selectedFolder?.name, projectName),
      };
    }

    const baseSettings = uxpStorage ?? defaultSettings;

    if (overrideFolderId) {
      return {
        ...baseSettings,
        assetFolderId: overrideFolderId,
        assetFolderName: normalizeRootFolderName(
          overrideFolder?.name,
          projectName
        ),
      };
    }

    return {
      ...baseSettings,
      assetFolderName: normalizeRootFolderName(
        baseSettings.assetFolderName,
        projectName
      ),
    };
  }, [
    pendingSettings,
    selectedFolder,
    uxpStorage,
    defaultSettings,
    overrideFolderId,
    overrideFolder,
    projectName,
  ]);

  const showResourceNotFoundToast = useResourceNotFoundToast();

  const updateExportSettings = useCallback(
    (partial: Partial<TSettings>) => {
      setPendingSettings(() => ({
        ...settings,
        ...partial,
        // When the folder changes, clear the name so the fragment lookup resolves the new one
        ...('assetFolderId' in partial && { assetFolderName: null }),
      }));
    },
    [settings]
  );

  const { error: folderAssetForExportLocationError } =
    useGetFolderAssetForExportLocationQuery({
      variables: {
        folderId: settings.assetFolderId ?? '',
      },
      fetchPolicy: 'network-only',
      skip: !settings.assetFolderId,
    });

  useEffect(() => {
    if (!folderAssetForExportLocationError) return;
    setPendingSettings(() => ({
      ...settings,
      assetFolderId: null,
      assetFolderName: null,
    }));
    showResourceNotFoundToast({});
    // Only fire when a new error arrives (mirrors `onError`), not on every
    // `settings`/`showResourceNotFoundToast` identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderAssetForExportLocationError]);

  const storeExportSettings = useCallback(
    async (partial?: Partial<TSettings>) => {
      const merged: TSettings = { ...settings, ...(partial ?? {}) };
      const { __typename: __omit, ...inputForMutation } = merged;

      const result = await storeMutation(inputForMutation);
      if (result) {
        setPendingSettings({
          ...settings,
          ...result,
        });
      }
    },
    [settings, storeMutation]
  );

  return {
    ...settings,
    assetFolderName: normalizeRootFolderName(
      settings.assetFolderName,
      projectName
    ),
    loading,
    projectId,
    onProjectIdChange,
    updateExportSettings,
    storeExportSettings,
  };
};
